import { useState, type ReactNode } from 'react';
import { CircleAlert, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog } from './ui/dialog';
import { Skeleton } from './ui/misc';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/lib/api';

export function EmptyState({
  icon,
  title,
  body,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mx-auto flex max-w-md flex-col items-center px-6 py-16 text-center',
        className,
      )}
    >
      {icon && (
        <div className="mb-5 grid h-16 w-16 place-items-center rounded-2xl bg-pink/10 text-pink [&_svg]:h-7 [&_svg]:w-7">
          {icon}
        </div>
      )}
      <h2 className="font-display text-2xl font-semibold">{title}</h2>
      {body && <div className="mt-2 text-[15px] leading-relaxed text-muted">{body}</div>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ErrorState({
  error,
  title = "We couldn't load this right now.",
  onRetry,
  className,
}: {
  error?: unknown;
  title?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      className={className}
      icon={<CircleAlert />}
      title={title}
      body={error ? errorMessage(error) : undefined}
      action={
        onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
        )
      }
    />
  );
}

export function RowSkeleton({ poster = true }: { poster?: boolean }) {
  return (
    <div className="px-page space-y-3">
      <Skeleton className="h-6 w-48 rounded-lg" />
      <div className="flex gap-3 overflow-hidden">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton
            key={i}
            className={cn(
              'shrink-0',
              poster ? 'aspect-[2/3] w-36 sm:w-44' : 'aspect-video w-64 sm:w-72',
            )}
          />
        ))}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel = 'Confirm',
  danger,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => Promise<unknown> | void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} description={body} size="sm">
      <div className="mt-2 flex justify-end gap-3">
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          variant={danger ? 'danger' : 'primary'}
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
              onOpenChange(false);
            } finally {
              setBusy(false);
            }
          }}
        >
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}
